import { defineResource } from '@tnn/core'
import { z } from 'zod'

/**
 * A trip (เที่ยวรถ): one truck carrying waybills between branches, from where it loads to its stops in order — a direct
 * run is one stop; a truck down south drops at several (DC กรุงเทพ → สุราษฎร์ → นครศรีฯ). Each bill is scanned on at
 * loading and off at its stop (`waybill_event`, tracking.ts). Submitting numbers it `T6910-0001`: from then each
 * branch on its way loads and unloads its own bills, with no state of the truck to keep — no departing, no arriving,
 * no ending. What it costs (ค่าเที่ยว, fuel, tolls) dispatch writes as a draft bill to accounting (`costs`).
 */

export const trip = defineResource({
  app: 'tms',
  name: 'trip',
  path: 'trips',
  schema: z.object({
    date: z.iso.date(),
    originBranchId: z.string().min(1),
    /** Where it unloads, in order (branch ids) */
    stops: z.array(z.string().min(1)).min(1).max(12),
    vehicleId: z.string().nullish(),
    driverId: z.string().nullish(),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 124, priority: 1 } },
    date: { filter: true, list: { width: 120, priority: 2 }, form: { span: 4 } },
    originBranchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { priority: 2 },
      form: { span: 8 },
    },
    stops: {
      type: 'multiselect',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      list: { priority: 1 },
      form: { span: 12, custom: true },
    },
    vehicleId: {
      type: 'ref',
      ref: { resource: 'tms.vehicle', where: { status: 'active' } },
      filter: true,
      list: { width: 120, priority: 2 },
      // Picked from plates only (/api/tms/crew): the counter does not read vehicle records
      form: { span: 6, custom: true },
    },
    driverId: {
      type: 'ref',
      ref: { resource: 'tms.driver', where: { status: 'active', kind: 'driver' } },
      filter: true,
      search: true,
      list: { width: 170, priority: 2 },
      form: { span: 6, custom: true },
    },
    /**
     * What the trip costs, line by line (ค่าเที่ยว, ค่าน้ำมัน, ค่าทางด่วน…: `TripCost[]`), set with its bill
     * (`contractorBillId`, ใบตั้งหนี้ค่าขนส่ง): one line on it, these in its description. Seen by whoever reads crew records
     */
    costs: { readOnly: true, type: 'json', list: false, export: false },
    contractorBillId: { readOnly: true, type: 'ref', ref: { resource: 'accounting.bill' }, list: false },
    /** On board now: bills and pieces */
    bills: { readOnly: true, type: 'integer', list: { width: 80, align: 'end', priority: 2 } },
    pieces: { readOnly: true, type: 'integer', list: { width: 84, align: 'end', priority: 2 } },
    /** Pieces counted short at a stop and not found since */
    shortPieces: { readOnly: true, type: 'integer', filter: true, list: { width: 90, align: 'end', priority: 2 } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'number',
  columns: ['number', 'stops', 'date', 'vehicleId', 'driverId', 'bills', 'pieces', 'shortPieces', 'originBranchId'],
  defaultSort: '-date',
  // `scan`: load and unload bills, make its ใบคุมรถ (@branch: at one's home branch); `cost`: write what it costs as a
  // draft bill to accounting. Its driver does not use the app
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'scan', 'cost'],
  branchFields: ['originBranchId'],
  // Its truck, driver and stops can change on the way (stops where bills went on or off stay)
  submittable: { allowOnSubmit: ['stops', 'vehicleId', 'driverId', 'notes'] },
  card: { title: 'number', subtitle: 'vehicleId', meta: ['date', 'bills', 'pieces'] },
})

/** Withholding on transport (ค่าขนส่ง) a contract driver is paid, their advances too */
export const transportWhtRate = 1

/** What a trip costs can be: the kinds to pick from, any other written out (`other`) */
export const tripCostKinds = ['trip', 'fuel', 'toll', 'other'] as const
export type TripCostKind = (typeof tripCostKinds)[number]

/** A line of what a trip costs: its kind, a name for `other`, and the amount */
export const tripCostSchema = z.object({
  kind: z.enum(tripCostKinds),
  name: z.string().trim().max(80).nullish(),
  amount: z.number().positive().max(1_000_000),
})
export type TripCost = z.infer<typeof tripCostSchema>
